import { ValueType } from '../FormatterTypes';
import { asBoolean, booleanToString, tryStringToBoolean } from './ValueUtils';

/**
 * Parses a params string into an object.
 *
 * @param paramString the param string
 */
export function parseParamList(paramString: String) {
  const params: Record<string, string> = {};
  let currentPos = 0;

  while (currentPos < paramString.length) {
    // Skip leading whitespace
    while (
      currentPos < paramString.length &&
      /\s/.test(paramString[currentPos])
    ) {
      currentPos++;
    }

    if (currentPos >= paramString.length) break;

    // Parse name
    let nameEnd = paramString.indexOf('=', currentPos);
    if (nameEnd === -1) break;

    const name = paramString.slice(currentPos, nameEnd).trim();
    currentPos = nameEnd + 1;

    // Parse value
    let value = '';
    if (paramString[currentPos] === '"') {
      currentPos++; // Skip opening quote

      while (currentPos < paramString.length) {
        if (paramString[currentPos] === '"') {
          // Check if this quote is escaped by being followed by another quote
          if (
            currentPos + 1 < paramString.length &&
            paramString[currentPos + 1] === '"'
          ) {
            value += '"';
            currentPos += 2;
          } else {
            currentPos++; // Skip closing quote
            break;
          }
        } else {
          value += paramString[currentPos];
          currentPos++;
        }
      }
    }

    params[name] = value;

    // Skip to next parameter
    while (currentPos < paramString.length && paramString[currentPos] !== ';') {
      currentPos++;
    }
    currentPos++; // Skip semicolon
  }

  return params;
}

/**
 * Sets a boolean parameter value in the params string.
 *
 * @param name Name of the param that should be set
 * @param value The value that should be set.
 * @param params The string that the name="value" string should be inserted into
 */
export function formatterSetBoolParam(
  name: string,
  value: ValueType,
  params: string
): string {
  const valAsBoolStr = booleanToString(asBoolean(value) || false);
  return setParamValue(name, valAsBoolStr, params);
}

/**
 * Sets, or removes a parameter
 * @param name Name of the param that should be set
 * @param value The value that should be set. If empty string, the name parameter will be removed from
 * @param params The string that the name="value" string should be inserted into
 */
export function setParamValue(
  name: string,
  value: string,
  params: string
): string {
  if (value === '') {
    return removeParamValue(name, params);
  } else {
    return addParamValue(name, value, params);
  }
}

// MANIPULATING PARAMS

/**
 *  Adds parameter "name" with value "value" to "params"
 */
export function addParamValue(
  name: string,
  value: string,
  params: string
): string {
  let { result, value: oldValue } = formatterStrParam(name, params, '');
  if (result) {
    return params.replace(
      new RegExp(`${name}="${oldValue}"`),
      `${name}="${value}"`
    );
  } else {
    return `${params};${name}="${value}"`;
  }
}

/**
 * Removes the parameter "name" from "params"
 */
export function removeParamValue(name: string, params: string): string {
  return params
    .replace(new RegExp(`${name}="[^"]*"`), '')
    .replace(';;', ';')
    .replace(/^;+|;+$/g, ''); // Remove any
}

// GETTING PARAMETER VALUES

/**
 * Get a string value from the params string, based on name. Starts from index 0.
 */
export function formatterStrParam(
  name: string,
  params: string,
  defaultParams: string
): { result: boolean; value: string } {
  return formatterStrParamFrom(0, name, params, defaultParams);
}

/**
 * Gets the value for a parameter, based on name, or defaultValue if it doesn't exist
 */
export function formatterStrParamDef(
  name: string,
  params: string = '',
  defaultParams: string = '',
  defaultValue: string = ''
): string {
  const { result, value } = formatterStrParam(name, params, defaultParams);

  return result ? value : defaultValue;
}

/**
 * Get a string value from the params string. Starts looking from index 'from'
 */
export function formatterStrParamFrom(
  from: number,
  name: string,
  params: string = '',
  defaultParams: string = ''
): { result: boolean; value: string } {
  let regExp = new RegExp(`(?:^|;)${name}="([^"]*)"`, 'i');
  let result = regExp.exec(params.slice(from));

  if (result) {
    return { result: true, value: result[1] };
  } else {
    const defaultParamValue = regExp.exec(defaultParams);

    if (defaultParamValue) {
      return { result: true, value: defaultParamValue[1] };
    } else {
      return { result: false, value: '' };
    }
  }
}

/**
 * Get an integer from the params or defaultParams string. If 'name' parameter doesn't exist,
 * or is not an integer, defaultValue is returned
 */
export function formatterIntParamDef(
  name: string,
  params: string,
  defaultParams: string,
  defaultValue: number = 0
): number {
  const { result, value } = formatterStrParam(name, params, defaultParams);

  // If we got a hit from params, and the value of that his was an integer, return it. Otherwise returns 'defaultValue'
  if (result && value !== '') {
    const intVal = Number(value);
    return intVal != null && Number.isInteger(intVal) ? intVal : defaultValue;
  } else {
    return defaultValue;
  }
}

/**
 * Get a boolean parameter from the 'params' or 'defaultParams' strings. If it can't be found or converted to boolean,
 * returns 'defaultValue'
 */
export function formatterBoolParamDef(
  name: string,
  params: string,
  defaultParams: string,
  defaultValue: boolean = false
): boolean {
  const { result, value } = formatterStrParam(name, params, defaultParams);

  if (result && value !== '') {
    const boolVal = tryStringToBoolean(value);
    return boolVal !== null ? boolVal : defaultValue;
  } else {
    return defaultValue;
  }
}

// Helpers for FormatGroupedNr

/**
 * Interface for storing key/value pairs.
 */
export interface IParamKeyValuePair {
  paramName: string;
  paramValue: string;
}

/**
 * Get a list of parameters within a parameter,
 * e.g: Groups="a=12;b=20".
 * @param baseParam The name of the outer parameter
 * @param params First choice of string to look for 'name'
 * @param defaultParams If 'name' is not found in params, will look here
 * @returns List of IParamKeyValuePair. If 'name' isn't found or there are no parameters within it,
 * this will be an empty list. Key will be the groupname, and value will be the group value
 */
export function formatterParamParamNext(
  baseParam: string,
  params: string,
  defaultParams: string
): IParamKeyValuePair[] {
  return findInnerParams(baseParam, params, defaultParams, '=', ';');
}

function findInnerParams(
  baseParam: string,
  params: string,
  defaultParams: string,
  valueSep: string = '=',
  paramSep: string = ';'
): IParamKeyValuePair[] {
  const { result: paramExist, value: paramSection } = formatterStrParam(
    baseParam,
    params,
    defaultParams
  );

  // Look for key value pairs, separated by valueSep
  let regExp = new RegExp(`(?:([^=]+)${valueSep}([^;$]+))${paramSep}?`, 'g');
  // let regExp = /(?:([a-ö\/]+)=(\d+));?/g;

  if (paramExist) {
    return paramFindKeyValuePairs(regExp, paramSection);
  } else {
    return [];
  }
}

/**
 * Finds an inner parameter called 'findParam' inside 'baseParam'. Returns null otherwise
 */
export function formatterParamParamFind2(
  baseParam: string,
  findParam: string,
  params: string,
  defaultParams: string
): { paramName: string; paramValue: string } | null {
  let result: { paramName: string; paramValue: string } | null = null;
  const innerParams = findInnerParams(
    baseParam,
    params,
    defaultParams,
    '=',
    ';'
  );
  innerParams.forEach(({ paramName, paramValue }) => {
    if (paramName === findParam) {
      result = {
        paramName: paramName.trim(),
        paramValue: paramValue.trim(),
      };
    }
  });

  return result;
}

/**
 * Runs regExp until no more matches are found and returns a list of IParamKeyValuePair
 */
function paramFindKeyValuePairs(
  regExp: RegExp,
  str: string
): IParamKeyValuePair[] {
  const results: IParamKeyValuePair[] = [];

  // Run the regexp until we have no more matches
  let regExpResult: any;
  do {
    regExpResult = regExp.exec(str);
    if (regExpResult) {
      results.push({
        paramName: regExpResult[1],
        paramValue: regExpResult[2],
      });
    }
  } while (regExpResult);

  return results;
}
